import React from 'react';
import PropTypes from 'prop-types';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Table from 'react-bootstrap/Table';
import Card from 'react-bootstrap/Card';
import { Link } from 'react-router-dom';
import CardTitleContainer from '../lib/CardTitleContainer';
import AccountIcon from '../lib/AccountIcon';
import AccountTag from '../lib/AccountTag';
import { FormattedNumber } from 'react-intl';
import VerifiedBadge from '../lib/VerifiedBadge';
import XBadge from '../lib/XBadge';
import XCopyButton from '../lib/XCopyButton';
import MPTokenTag from '../lib/MPTokenTag';
import TestnetValueTag from '../lib/TestnetValueTag';
import { isXrplMainnet } from '../common/Helpers';

const MPTokenInfo = (props) => {
	const { mpt } = props;
	const mptMetadata = mpt?.MPTokenMetadataJSON;
	const Ticker = mpt?.Ticker || mptMetadata?.ticker;
	const mptFlags = mpt?._Flags || {};

	return (
		<Card className="shadow-sm">
			<Card.Body>
				<Card.Title>
					<CardTitleContainer icon="square-dollar" title="Multi-purpose token issuance" />
				</Card.Title>
				<Row>
					<Col xs={12} md={6}>
						<Table responsive>
							<thead>
								{Ticker &&
								<tr>
									<th className="data-header">Ticker:</th>
									<th>
										<span className='text-monospace'>
											<Link to={`/mpt/${mpt?.mpt_issuance_id}`}>${Ticker}</Link>
										</span>
										{mpt?.IssuerAccount?.verified &&
											<VerifiedBadge />
										}
									</th>
								</tr>
								}
							</thead>

							<tbody>
								{mpt?.mpt_issuance_id &&
								<tr>
									<td>Issuance ID:</td>
									<td>
										<Link to={`/mpt/${mpt?.mpt_issuance_id}`}>{mpt.mpt_issuance_id}</Link>
										<XCopyButton text={mpt?.mpt_issuance_id} />
										<XCopyButton url={`/mpt/${mpt?.mpt_issuance_id}`} />
									</td>
								</tr>
								}

								{mpt.Issuer &&
								<tr>
									<td>Issuer:</td>
									<td>
										<AccountTag name={mpt?.IssuerAccount} link>{mpt.Issuer}</AccountTag>
									</td>
								</tr>
								}

								{mpt?.AssetScale >= 0 &&
								<tr>
									<td>Asset scale:</td>
									<td>
										{mpt.AssetScale}
									</td>
								</tr>
								}

								{mpt?.TransferFee >= 0 &&
								<tr>
									<td>Transfer fee:</td>
									<td className='text-monospace'>
										<FormattedNumber value={mpt.TransferFee / 1000} minimumFractionDigits={3} />%
									</td>
								</tr>
								}

								{mpt?.index &&
								<tr>
									<td>Object:</td>
									<td>
										<span className='address-tag'>
											<Link to={`/object/${mpt.index}`}>{mpt.index}</Link>
										</span>
									</td>
								</tr>
								}
							</tbody>
						</Table>

						{ mptMetadata &&
						<Table responsive>
							<thead>
							<tr className='table-light'>
								<th colSpan="2">
									<XBadge variant="primary" className="mr-1">
										<a href="https://xls.xrpl.org/xls/XLS-0089-multi-purpose-token-metadata-schema.html" target="_blank" rel="noopener noreferrer nofollow">XLS-89</a>
									</XBadge>
									MPT Metadata
								</th>
							</tr>
							</thead>
							<tbody>
							{mptMetadata?.name &&
								<tr>
									<td>Name:</td>
									<td>
										{mptMetadata.name}
									</td>
								</tr>
							}

							{mptMetadata?.desc &&
								<tr>
									<td>Description:</td>
									<td>
										{mptMetadata.desc}
									</td>
								</tr>
							}

							{mptMetadata?.issuer_name &&
								<tr>
									<td>Issued by:</td>
									<td>
										{mptMetadata.issuer_name}
									</td>
								</tr>
							}

							{(mptMetadata?.asset_class || mptMetadata?.asset_subclass) &&
								<tr>
									<td className='text-nowrap'>Asset class:</td>
									<td>
										{ mptMetadata?.asset_class && <span className='text-uppercase mr-2'><XBadge variant="info">{mptMetadata.asset_class}</XBadge></span> }
										{ mptMetadata?.asset_subclass && <span className='text-uppercase mr-2'><XBadge variant="info">{mptMetadata.asset_subclass}</XBadge></span> }
									</td>
								</tr>
							}
							</tbody>
						</Table>
						}
					</Col>

					<Col xs={12} md={2}>
						<Table responsive>
							<thead>
								<tr className='text-left'>
									<th colSpan={2}>MPT Settings</th>
								</tr>
							</thead>
							<tbody>
								<tr className='text-left'>
									<td>Issuer can lock:</td>
									<td>
										<XBadge variant={mptFlags?.lsfMPTCanLock ? "success" : "warning"}>
											{mptFlags?.lsfMPTCanLock ? "YES" : "NO"}
										</XBadge>
									</td>
								</tr>
								<tr className='text-left'>
									<td>Requires auth:</td>
									<td>
										<XBadge variant={mptFlags?.lsfMPTRequireAuth ? "success" : "warning"}>
											{mptFlags?.lsfMPTRequireAuth ? "YES" : "NO"}
										</XBadge>
									</td>
								</tr>
								<tr className='text-left'>
									<td>MPT Escrow:</td>
									<td>
										<XBadge variant={mptFlags?.lsfMPTCanEscrow ? "success" : "warning"}>
											{mptFlags?.lsfMPTCanEscrow ? "YES" : "NO"}
										</XBadge>
									</td>
								</tr>
								<tr className='text-left'>
									<td>MPT trading:</td>
									<td>
										<XBadge variant={mptFlags?.lsfMPTCanTrade ? "success" : "warning"}>
											{mptFlags?.lsfMPTCanTrade ? "YES" : "NO"}
										</XBadge>
									</td>
								</tr>
								<tr className='text-left'>
									<td>MPT transfer:</td>
									<td>
										<XBadge variant={mptFlags?.lsfMPTCanTransfer ? "success" : "warning"}>
											{mptFlags?.lsfMPTCanTransfer ? "YES" : "NO"}
										</XBadge>
									</td>
								</tr>
								<tr className='text-left'>
									<td>MPT clawback:</td>
									<td>
										<XBadge variant={mptFlags?.lsfMPTCanClawback ? "success" : "warning"}>
											{mptFlags?.lsfMPTCanClawback ? "YES" : "NO"}
										</XBadge>
									</td>
								</tr>
							</tbody>
						</Table>
					</Col>

					<Col xs={12} md={4}>
						<Table responsive className='mb-0'>
							<thead>
								<tr className="table-secondary">
									<td colSpan="1" className="text-center">
										<AccountIcon
											url={mptMetadata?.icon}
											isMPT={true}
											rounded={true}
											roundedCircle={false}
											height={113}
											width={113}
										/>
									</td>
								</tr>
							</thead>
						</Table>
						<Table responsive>
							<tbody>
								{mpt?.OutstandingAmount >= 0 && 
								<tr>
									<td>Outstanding:</td>
									<td className='text-right'>
										<MPTokenTag
											mpt_issuance_id={mpt?.mpt_issuance_id}
											AssetScale={mpt?.AssetScale}
											Ticker={Ticker}
										>
											{mpt.OutstandingAmount}
										</MPTokenTag>
									</td>
								</tr>
								}
								{mpt?.MaximumAmount >= 0 &&
								<tr className='table-success'>
									<td>Max Amount:</td>
									<td className='text-right'>
										<MPTokenTag
											mpt_issuance_id={mpt?.mpt_issuance_id}
											AssetScale={mpt?.AssetScale}
											Ticker={Ticker}
										>
											{mpt.MaximumAmount}
										</MPTokenTag>

									</td>
								</tr>
								}

								{!isXrplMainnet() &&
								<tr className="text-center table-secondary">
									<td colSpan={2}>
										<TestnetValueTag/>
									</td>
								</tr>
								}
							</tbody>
						</Table>
					</Col>
				</Row>
			</Card.Body>
		</Card>
	);
}


MPTokenInfo.propTypes = {
	mpt: PropTypes.object.isRequired,
}

export default MPTokenInfo;
